/**
 * BackupDetailPage — 备份详情
 * 展示备份完整信息；下载 / 删除操作放在此处（列表行不再直接提供）。
 */
import type { ReactNode } from 'react'
import { useState } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Spinner,
  Badge,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
} from '@fluentui/react-components'
import {
  ArrowDownloadRegular,
  DeleteRegular,
  CloudRegular,
  DatabaseRegular,
} from '@fluentui/react-icons'
import { backupsApi } from '@/api/backups'
import type { Backup } from '@/api/types'
import { toast } from '@/lib/toast'
import { ApiError } from '@/api/client'
import { ROUTES } from '@/routes'
import DetailLayout, { DetailSection, DetailRow } from '@/components/DetailLayout'
import DangerButton from '@/components/DangerButton'
import { useDetailItem } from '@/hooks/useDetailItem'
import { formatDate, formatBytes } from '@/lib/format'

// ── 辅助函数 ──
const BACKUP_TYPE_LABELS: Record<string, string> = {
  full: '完整备份',
  database: '数据库',
  blobs: 'Blob 存储',
  config: '配置',
}

type BadgeColor = 'brand' | 'success' | 'danger' | 'warning' | 'informative'

function backupTypeIcon(type: string): ReactNode {
  switch (type) {
    case 'blobs':
      return <CloudRegular />
    default:
      return <DatabaseRegular />
  }
}

function backupStatusColor(status: string): BadgeColor {
  switch (status) {
    case 'completed':
      return 'success'
    case 'running':
      return 'warning'
    case 'failed':
      return 'danger'
    case 'pending':
      return 'brand'
    default:
      return 'informative'
  }
}

const useStyles = makeStyles({
  dialogActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    paddingTop: '8px',
  },
  deleteName: {
    fontWeight: 600,
    color: tokens.colorStatusDangerForeground1,
  },
})

export default function BackupDetailPage() {
  const styles = useStyles()
  const { id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const backupId = id as string

  const { item, isLoading } = useDetailItem<Backup>(
    ['backups'],
    () => backupsApi.list(),
    id as string,
    'id',
  )

  const [deleteOpen, setDeleteOpen] = useState(false)

  const downloadMutation = useMutation({
    mutationFn: () => backupsApi.download(backupId),
    onSuccess: () => {
      toast.success('下载成功', '备份文件已开始下载')
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : '下载失败，请稍后重试'
      toast.error('下载失败', msg)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => backupsApi.delete(backupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backups'] })
      toast.success('删除成功', '备份已删除')
      navigate(ROUTES.BACKUPS)
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : '删除失败，请稍后重试'
      toast.error('删除失败', msg)
    },
  })

  const stateItem = (location.state as { item?: Backup } | null)?.item
  const current = item ?? stateItem

  return (
    <>
      <DetailLayout
        title={`${current ? (BACKUP_TYPE_LABELS[current.backup_type] ?? current.backup_type) : '备份'} · ${id}`}
        subtitle="备份详情"
        media={backupTypeIcon(current?.backup_type ?? '')}
        badge={
          current ? (
            <Badge appearance="filled" color={backupStatusColor(current.status ?? '')}>
              {current.status}
            </Badge>
          ) : undefined
        }
        loading={isLoading && !stateItem}
        notFound={!isLoading && !item && !stateItem}
        notFoundText="未找到该备份"
        actions={
          <>
            <Button
              appearance="subtle"
              icon={
                downloadMutation.isPending ? (
                  <Spinner size="tiny" />
                ) : (
                  <ArrowDownloadRegular />
                )
              }
              onClick={() => downloadMutation.mutate()}
            >
              下载
            </Button>
            <Button
              appearance="subtle"
              icon={<DeleteRegular />}
              onClick={() => setDeleteOpen(true)}
            >
              删除
            </Button>
          </>
        }
      >
        <DetailSection title="备份信息">
          <DetailRow label="ID" value={current?.id} />
          <DetailRow
            label="类型"
            value={current ? (BACKUP_TYPE_LABELS[current.backup_type] ?? current.backup_type) : undefined}
          />
          <DetailRow label="状态" value={current?.status} />
          <DetailRow label="加密" value={current?.encrypted ? '是' : '否'} />
          <DetailRow label="文件大小" value={formatBytes(current?.file_size)} />
          <DetailRow label="创建时间" value={formatDate(current?.created_at)} />
          <DetailRow label="完成时间" value={formatDate(current?.completed_at)} />
          <DetailRow label="错误信息" value={current?.error} full />
        </DetailSection>
      </DetailLayout>

      {/* 删除确认 */}
      <Dialog
        open={deleteOpen}
        onOpenChange={(_, d) => {
          if (!d.open) setDeleteOpen(false)
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>确认删除</DialogTitle>
            <DialogContent>
              <Text>
                确定要删除备份{' '}
                <span className={styles.deleteName}>
                  {current
                    ? `${BACKUP_TYPE_LABELS[current.backup_type] ?? current.backup_type} · ${current.id}`
                    : ''}
                </span>{' '}
                吗？此操作不可撤销。
              </Text>
            </DialogContent>
            <div className={styles.dialogActions}>
              <Button onClick={() => setDeleteOpen(false)}>取消</Button>
              <DangerButton
                loading={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate()}
              >
                确认删除
              </DangerButton>
            </div>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  )
}
